import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Shield, Lock, Eye, Trash2, Mail } from 'lucide-react';
import Layout from '../components/Layout';
import { useCompanyInfo } from '../contexts/CompanyInfoContext';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const companyInfo = useCompanyInfo();
  const contact = companyInfo?.contact || {};
  const address = companyInfo?.address || {};
  const name = companyInfo?.company_name || 'Selskapet';

  return (
    <Layout>
      <Helmet>
        <title>Personvernerklæring | {name}</title>
        <meta name="description" content={`Les om hvordan ${name} behandler dine personopplysninger`} />
      </Helmet>

      <div className="min-h-screen bg-black">
        <div className="relative bg-gradient-to-br from-gray-900 via-black to-blue-900/20 pt-32 pb-20">
          <div className="container mx-auto px-4">
            <button onClick={() => navigate(-1)} className="text-gray-400 hover:text-white mb-8">← Tilbake</button>
            <h1 className="text-5xl font-bold mb-6">
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                Personvernerklæring
              </span>
            </h1>
            <p className="text-xl text-gray-300">Sist oppdatert: {new Date().toLocaleDateString('no-NO', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
        </div>

        <div className="container mx-auto px-4 py-20 max-w-4xl">
          <div className="prose prose-invert prose-lg max-w-none">
            <div className="bg-gray-800/50 p-6 rounded-lg mb-8 border border-gray-700">
              <div className="flex items-center space-x-3 mb-4">
                <Shield className="w-8 h-8 text-blue-400" />
                <h2 className="text-2xl font-bold text-white m-0">Om denne erklæringen</h2>
              </div>
              <p className="text-gray-300">
                Sand-tec AS er opptatt av å beskytte ditt personvern. Denne erklæringen beskriver hvordan vi samler inn, bruker og beskytter dine personopplysninger i samsvar med GDPR.
              </p>
            </div>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-4 flex items-center">
                <Eye className="w-6 h-6 mr-3 text-blue-400" />
                Hvilke opplysninger samler vi?
              </h2>
              <div className="bg-gray-800/30 p-6 rounded-lg space-y-4">
                <div>
                  <h3 className="text-xl font-semibold text-white">Booking-informasjon:</h3>
                  <ul className="text-gray-300 list-disc list-inside">
                    <li>Navn, telefonnummer, e-postadresse</li>
                    <li>Kjøretøytype og bookingdetaljer</li>
                    <li>Dato og tidspunkt for avtale</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">Teknisk informasjon:</h3>
                  <ul className="text-gray-300 list-disc list-inside">
                    <li>IP-adresse</li>
                    <li>Nettlesertype og enhet</li>
                    <li>Sidevisninger og interaksjoner (via Google Analytics)</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-4 flex items-center">
                <Lock className="w-6 h-6 mr-3 text-blue-400" />
                Hvordan bruker vi opplysningene?
              </h2>
              <div className="bg-gray-800/30 p-6 rounded-lg">
                <ul className="text-gray-300 space-y-2">
                  <li>✓ Behandle og bekrefte bookinger</li>
                  <li>✓ Sende e-postvarsler om bookinger</li>
                  <li>✓ Forbedre nettstedet og brukeropplevelsen</li>
                  <li>✓ Analysere trafikk og bruksmønstre</li>
                  <li>✓ Oppfylle juridiske forpliktelser</li>
                </ul>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-4 flex items-center">
                <Trash2 className="w-6 h-6 mr-3 text-red-400" />
                Dine rettigheter
              </h2>
              <div className="bg-gray-800/30 p-6 rounded-lg">
                <p className="text-gray-300 mb-4">Under GDPR har du følgende rettigheter:</p>
                <ul className="text-gray-300 space-y-2">
                  <li><strong className="text-white">Innsyn:</strong> Be om kopi av dine personopplysninger</li>
                  <li><strong className="text-white">Retting:</strong> Be om at uriktige opplysninger rettes</li>
                  <li><strong className="text-white">Sletting:</strong> Be om at dine opplysninger slettes</li>
                  <li><strong className="text-white">Dataportabilitet:</strong> Motta dine data i et strukturert format</li>
                  <li><strong className="text-white">Begrensning:</strong> Be om at behandlingen begrenses</li>
                </ul>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Informasjonskapsler (Cookies)</h2>
              <div className="bg-gray-800/30 p-6 rounded-lg">
                <p className="text-gray-300 mb-4">Vi bruker informasjonskapsler for:</p>
                <ul className="text-gray-300 space-y-2">
                  <li><strong className="text-white">Nødvendige cookies:</strong> For å gjøre nettstedet fungerende</li>
                  <li><strong className="text-white">Analyse cookies:</strong> Google Analytics for å forstå hvordan du bruker siden</li>
                  <li><strong className="text-white">Funksjonelle cookies:</strong> Huske dine preferanser</li>
                </ul>
                <p className="text-gray-300 mt-4">Du kan når som helst endre dine cookie-innstillinger i nettleseren.</p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-white mb-4">Lagring og sikkerhet</h2>
              <div className="bg-gray-800/30 p-6 rounded-lg">
                <p className="text-gray-300">Vi lagrer dine personopplysninger sikkert og vil ikke dele dem med tredjeparter uten ditt samtykke, bortsett fra når det er nødvendig for å levere tjenesten (f.eks. e-postleveranse).</p>
                <p className="text-gray-300 mt-4">Booking-data lagres i 3 år for regnskapsformål.</p>
              </div>
            </section>

            <section className="bg-gradient-to-r from-blue-900/30 to-cyan-900/30 p-8 rounded-lg border border-blue-700/50">
              <div className="flex items-center space-x-3 mb-4">
                <Mail className="w-8 h-8 text-blue-400" />
                <h2 className="text-2xl font-bold text-white m-0">Kontakt oss</h2>
              </div>
              <p className="text-gray-300 mb-4">Har du spørsmål om personvern eller ønsker å utøve dine rettigheter?</p>
              <div className="space-y-2 text-gray-300">
                {contact.email && <p><strong className="text-white">E-post:</strong> {contact.email}</p>}
                {contact.phone && <p><strong className="text-white">Telefon:</strong> {contact.phone}</p>}
                {address.street && <p><strong className="text-white">Adresse:</strong> {address.street}, {address.postal_code} {address.city}</p>}
              </div>
            </section>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PrivacyPolicy;